import React, { ChangeEvent, forwardRef, ReactNode, Ref } from 'react';
import { InheritableElementProps } from '../_internal/components';
import Box from '../Box';
import IconError16 from '../icons/IconError16';
import { MarginProps } from '../_internal/spacing';
import {
  processStylingProps,
  DeprecatedAndDangerousStylingProps,
} from '../_internal/styling';

import styles from './textField.module.scss';

interface CommonProps extends DeprecatedAndDangerousStylingProps {
  /**
   * Provides additional context about the field
   * or provides feedback to the user about the error
   */
  helpText?: ReactNode;
  /**
   * Whether or not to visually hide the label in the UI.
   * It will be added as an ARIA attribute for accessibility
   *
   * @default false
   */
  hideLabel?: boolean;
  /**
   * The field ID. If specified, this must be unique on the page. It defaults to the `name` if unspecified.
   */
  id?: string;
  /**
   * The field label content
   */
  label: ReactNode;
  /**
   * The form name of the field
   */
  name: string;
  /**
   * The current state of the text field.
   *
   * - `default`: The default look & feel
   * - `error`: Displays visually in an error state
   * - `disabled`: Visually disabled & prevents interaction
   *
   * @default 'default'
   */
  state?: 'default' | 'error' | 'disabled';
  /**
   * Value of the text field
   */
  value: string;
}

interface BaseInputProps extends CommonProps {
  rows?: undefined;
  /**
   * A handler for when the text field value changes.
   * (The raw `event` is included as the second parameter, if needed.)
   */
  onChange: (value: string, e: ChangeEvent<HTMLInputElement>) => void;
}

interface BaseTextareaProps extends CommonProps {
  /**
   * Transform the text input to a text area with the specified rows
   */
  rows: number;
  onChange: (value: string, e: ChangeEvent<HTMLTextAreaElement>) => void;
}

// a text input cannot be both disabled & errored (based on designs)
// so we have a `state` prop that is one or the other in order to
// make impossible states impossible!
// https://kentcdodds.com/blog/make-impossible-states-impossible
type InputProps = Omit<
  InheritableElementProps<'input', BaseInputProps>,
  'disabled'
>;
type TextAreaProps = Omit<
  InheritableElementProps<'textarea', BaseTextareaProps>,
  'disabled'
>;

type Props = InputProps | TextAreaProps;

export type TextFieldProps = Props & MarginProps;

/**
 * Text fields allow users to enter and edit text in a UI. They can be single-row text inputs or multi-row text areas. They typically appear in forms and dialogs.
 */
const TextField = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  TextFieldProps
>(
  (
    {
      helpText,
      hideLabel = false,
      name,
      id = name,
      label,
      state = 'default',
      m,
      mt,
      mb,
      my,
      ml,
      mr,
      mx,
      ...fieldProps
    },
    ref,
  ) => {
    const hasError = state === 'error';
    const helpTextId = helpText ? `${id}-help-text` : undefined;
    const labelId = `${id}-label`;
    const commonFieldProps = {
      id,
      name,
      disabled: state === 'disabled',
      'aria-invalid': hasError || undefined,
      'aria-describedby': helpTextId,

      // NOTE: The `label` props is a React node, not a simple string,
      // so we cannot use `aria-label`. Therefor we use `aria-labelledby`
      // to point back to the `<label>` in order to trigger the
      // "hidden label" styling from `mode-style-system`.
      ...(hideLabel ? { 'aria-labelledby': labelId } : undefined),
    };
    const marginProps = { m, mt, mb, my, ml, mr, mx };
    let field: JSX.Element;

    // NOTE: `rows` is how TS knows whether or not we have `InputProps` vs
    // `TextAreaProps`. We need to check its existence on the props object
    // in order to know which other props are available, particularly the type of
    // `onChange`. This is also why we have to process the styling props w/in each
    // conditional instead of one time above.
    if (fieldProps.rows === undefined) {
      const { onChange, ...inputProps } = fieldProps;
      const processedInputProps = processStylingProps(inputProps, 'TextField', {
        stylingProps: 'warn',
        dangerousStylingProps: 'rewrite',
      });

      field = (
        <input
          {...processedInputProps}
          {...commonFieldProps}
          onChange={e => {
            e.persist();
            onChange(e.target.value, e);
          }}
          ref={ref as Ref<HTMLInputElement>}
        />
      );
    } else {
      const { rows, onChange, ...textAreaProps } = fieldProps;
      const processedTextAreaProps = processStylingProps(
        textAreaProps,
        'TextField',
        {
          stylingProps: 'warn',
          dangerousStylingProps: 'rewrite',
        },
      );

      field = (
        <textarea
          {...processedTextAreaProps}
          {...commonFieldProps}
          rows={rows}
          onChange={e => {
            e.persist();
            onChange(e.target.value, e);
          }}
          ref={ref as Ref<HTMLTextAreaElement>}
        />
      );
    }

    return (
      <Box className={styles.root} {...marginProps}>
        {field}
        <label id={labelId} htmlFor={id}>
          {label}
        </label>
        {helpText && (
          <p id={helpTextId}>
            {hasError && (
              <IconError16 data-testid="text-field-error-icon" title="Error" />
            )}
            {helpText}
          </p>
        )}
      </Box>
    );
  },
);

TextField.displayName = 'TextField';

export default TextField;
